import { newError } from '#app/lib/error'
import { commands } from '#app/radio'
import type { RelativeUrl } from '#server/types/common'
import { objectEntries } from './utils'

// Router logic borrowing from Backbone.Router and @jgarber/routerrouter

export type RouteControllerNames = Record<string, string>
export type RouteAction = (...args: string[]) => void | Promise<void>
export type RouteControllers = Record<string, RouteAction>

export function addRoutes (newRouteControllerNames: RouteControllerNames, controllers: RouteControllers) {
  for (const [ route, controllerName ] of objectEntries(newRouteControllerNames).reverse()) {
    const action = controllers[controllerName]
    setRoute(route, action)
  }
}

const handlers: [ RegExp, RouteAction ][] = []

function setRoute (route: string | RegExp, action: RouteAction) {
  if (!(route instanceof RegExp)) {
    route = routeToRegExp(route)
  }
  handlers.push([ route, action ])
}

function extractParameters (routeRegex: RegExp, pathname: string) {
  return routeRegex.exec(pathname).slice(1).map(parameter => {
    return parameter ? decodeURIComponent(parameter) : null
  })
}

// Adapted from https://github.com/jashkenas/backbone/blob/8f0285f/backbone.js#L1755-L1762
function routeToRegExp (route: string) {
  route = route
    // Escape
    .replace(/[-{}[\]+?.,\\^$|#\s]/g, '\\$&')
    // Optional  parameter
    .replace(/\((.*?)\)/g, '(?:$1)?')
    // Named parameter
    .replace(/(\(\?)?:\w+/g, (match, optional) => optional ? match : '([^/?]+)')
    // Splat parameter
    .replace(/\*\w+/g, '([^?]*?)')
  return new RegExp('^' + route + '(?:\\?([\\s\\S]*))?$')
}

let lastLoadedUrlAndHash: [ RelativeUrl, string ]
export async function loadUrl (pathname: string) {
  try {
    commands.execute('show:loader')
    pathname = decodeURIComponent(pathname)
    const [ pathnameWithoutHash, hash ] = pathname.split('#')
    if (hash) {
      // Keep track of the last hash, as the actions run hereafter might trigger calls to app.navigate
      // which will loose track of the hash, despite it being explicity passed to the parent `loadUrl` call
      lastLoadedUrlAndHash = [ pathnameWithoutHash as RelativeUrl, hash ]
    } else {
      lastLoadedUrlAndHash = undefined
    }
    for (const [ routeRegex, action ] of handlers) {
      if (routeRegex.test(pathnameWithoutHash)) {
        return await action(...extractParameters(routeRegex, pathnameWithoutHash))
      }
    }
    throw newError('route not found', 404, { pathnameWithoutHash, pathname, handlers })
  } catch (err) {
    commands.execute('show:error', err)
  }
}

export function getLastLoadedUrlWithoutHash () {
  return lastLoadedUrlAndHash?.[0]
}

export function getLastLoadedUrlHash (pathname: string) {
  if (lastLoadedUrlAndHash?.[0] === pathname) return lastLoadedUrlAndHash?.[1]
}

export async function startRouter () {
  // Reverse to let loadUrl iterate handlers by priority order
  handlers.reverse()
  await loadUrl(location.pathname + location.search + location.hash)
}
